// diceRoller.js
/**
 * Generate a single random die roll
 * @param {number} sides - Number of sides on the die
 * @returns {number} Random roll result
 */
function rollDie(sides) {
  return Math.floor(Math.random() * sides) + 1;
}

/**
 * Roll multiple dice and return all results
 * @param {number} count - Number of dice to roll
 * @param {number} sides - Number of sides on each die
 * @returns {Array} Array of individual die results
 */
function rollDice(count, sides) {
  const results = [];
  for (let i = 0; i < count; i++) {
    results.push(rollDie(sides));
  }
  return results;
}

/**
 * Calculate the modifier for an ability score
 * @param {number} score - The ability score
 * @returns {number} The calculated modifier
 */
function calculateModifier(score) {
  return Math.floor((score - 10) / 2);
}

/**
 * Generate ability scores based on the selected mode
 * @param {string} mode - 'normal', 'hard', 'nightmare', or 'epic'
 * @returns {Object} Object containing ability scores with roll details
 */
export function generateAbilityScores(mode = 'normal') {
  const abilities = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];
  const scores = {};
  
  for (const ability of abilities) {
    let rolls, keptDice, droppedDice, score;
    
    // Different handling based on mode
    if (mode === 'normal') {
      // Roll 4d6, keep highest 3, drop lowest
      rolls = rollDice(4, 6);
      rolls.sort((a, b) => b - a); // Sort descending
      keptDice = rolls.slice(0, 3);
      droppedDice = rolls.slice(3);
      score = keptDice.reduce((sum, roll) => sum + roll, 0);
      
      scores[ability] = {
        score,
        modifier: calculateModifier(score),
        rolls,
        keptDice,
        droppedDice
      };
    } else if (mode === 'hard') {
      // Use 3d6 for hard mode as requested
      rolls = rollDice(3, 6);
      score = rolls.reduce((sum, roll) => sum + roll, 0);
      // Hard mode has a ceiling of 14 before racial bonuses
      score = Math.min(score, 14);
      
      scores[ability] = {
        score,
        modifier: calculateModifier(score),
        rolls
      };
    } else if (mode === 'nightmare') {
      // Roll 4d6, keep lowest 3, drop highest
      rolls = rollDice(4, 6);
      rolls.sort((a, b) => a - b); // Sort ascending
      keptDice = rolls.slice(0, 3);
      droppedDice = rolls.slice(3);
      score = keptDice.reduce((sum, roll) => sum + roll, 0);
      
      scores[ability] = {
        score,
        modifier: calculateModifier(score),
        rolls,
        keptDice,
        droppedDice
      };
    } else if (mode === 'epic') {
      // Keep all 4d6
      rolls = rollDice(4, 6);
      score = rolls.reduce((sum, roll) => sum + roll, 0);
      
      scores[ability] = {
        score,
        modifier: calculateModifier(score),
        rolls
      };
    } else {
      // Default to normal mode if unrecognized
      rolls = rollDice(4, 6);
      rolls.sort((a, b) => b - a);
      keptDice = rolls.slice(0, 3);
      droppedDice = rolls.slice(3);
      score = keptDice.reduce((sum, roll) => sum + roll, 0);
      
      scores[ability] = {
        score,
        modifier: calculateModifier(score),
        rolls,
        keptDice,
        droppedDice
      };
    }
  }
  
  return scores;
}

/**
 * Generate starting gold based on character class
 * @param {Object} characterClass - The character class object
 * @returns {Object} Object with gold amount and roll details
 */
export function generateGold(characterClass) {
  if (!characterClass) return { total: 0 };
  
  // Default gold generation (3d4 × 10 gp)
  let diceCount = 3;
  let diceType = 4;
  let multiplier = 10;
  
  // Class-specific gold generation (if available in data)
  if (characterClass.startingGold === "abcde") {
    const goldData = characterClass.startingGold;
    diceCount = goldData.diceCount || diceCount;
    diceType = goldData.diceType || diceType;
    multiplier = goldData.multiplier || multiplier;
  } else {
    // Standard gold by class if specific data not available
    const className = characterClass.name || characterClass.class || '';
    const lowerClassName = className.toLowerCase();
    
    if (lowerClassName.includes('barbarian') || lowerClassName.includes('druid') || 
        lowerClassName.includes('monk') || lowerClassName.includes('sorcerer')) {
      diceCount = 3;
      diceType = 4;
      multiplier = 10;
    } else if (lowerClassName.includes('bard') || lowerClassName.includes('cleric') || 
               lowerClassName.includes('fighter') || lowerClassName.includes('paladin') || 
               lowerClassName.includes('ranger') || lowerClassName.includes('warlock')) {
      diceCount = 3;
      diceType = 4;
      multiplier = 10;
    } else if (lowerClassName.includes('rogue') || lowerClassName.includes('wizard')) {
      diceCount = 3;
      diceType = 4;
      multiplier = 10;
    } else if (lowerClassName.includes('artificer')) {
      diceCount = 3;
      diceType = 4;
      multiplier = 10;
    } else {
      diceCount = 3;
      diceType = 4;
      multiplier = 10;
    }
  }
  
  // Roll the dice
  const diceRolls = rollDice(diceCount, diceType);
  const total = diceRolls.reduce((sum, roll) => sum + roll, 0) * multiplier;
  
  return {
    total,
    diceRolls,
    diceCount,
    multiplier
  };
}
